import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styles from './SkillsForm.module.scss';
import Checkbox from '../../../../components/checkbox/Checkbox';
import TextAreaField from '../../../../components/textAreaField/TextAreaField';
import PropTypes from 'prop-types';

const LEARNING_OUTCOMES = [
  { key: 'obtainMedicalHistory', label: 'Obtain medical history' },
  { key: 'performPhysicalExamination', label: 'Perform physical examination' },
  { key: 'createDifferentialDiagnosis', label: 'Create differential diagnosis' },
  { key: 'deliverOralPresentation', label: 'Deliver oral presentation' },
  { key: 'interpretationOfLaboratoryData', label: 'Interpretation of laboratory data' },
  { key: 'interpretationOfRadiographicImaging', label: 'Interpretation of radiographic imaging' },
  { key: 'creationOfManagementPlan', label: 'Creation of management plan' },
  { key: 'documentationOfEncounter', label: 'Documentation of encounter' },
  { key: 'facilitateReferralToCommunityResources', label: 'Facilitate referral to community resources' },
];

const TECHNICAL_SKILLS = [
  { key: 'abscessIAndD', label: 'Abscess I&D' },
  { key: 'anesthesiaLocalDigitalBlock', label: 'Anesthesia – local digital block' },
  { key: 'antepartumFetalAssessment', label: 'Antepartum fetal assessment' },
  { key: 'applyCastSplint', label: 'Apply cast/splint' },
  { key: 'applyChangeSterileDressing', label: 'Apply/change sterile dressing' },
  { key: 'asepticAndIsolationTechniques', label: 'Aseptic and isolation techniques' },
  { key: 'cervicalCulturePAPSmear', label: 'Cervical culture/PAP smear' },
  { key: 'diabeticFootExam', label: 'Diabetic foot exam' },
  { key: 'digitalRectalExamHemocult', label: 'Digital rectal exam/hemocult' },
  { key: 'dopplerFetalHeartTones', label: 'Doppler fetal heart tones' },
  { key: 'dopplerUsAbi', label: 'Doppler us/abi' },
  { key: 'ekgPerformanceInterpretation', label: 'EKG performance/interpretation' },
  { key: 'fingerStickForGlucose', label: 'Finger stick for glucose' },
  { key: 'foleyCatheterInsertion', label: 'Foley catheter insertion' },
  { key: 'foreignBodyRemoval', label: 'Foreign body removal [skin, ear, nose, rectal]' },
  { key: 'fundalHeightMeasurement', label: 'Fundal height measurement' },
  { key: 'imIntradermalIVSubcutaneousInjection', label: 'IM/intradermal/IV/subcutaneous injection' },
  { key: 'insertNasalPacking', label: 'Insert nasal packing' },
  { key: 'insertNGTube', label: 'Insert NG tube' },
  { key: 'ivCatheterInsertion', label: 'IV catheter insertion' },
  { key: 'nebulizerTreatment', label: 'Nebulizer treatment' },
  { key: 'peakFlowMeasurement', label: 'Peak flow measurement' },
  { key: 'pelvicExam', label: 'Pelvic exam' },
  { key: 'pregnancyTest', label: 'Pregnancy test' },
  { key: 'urinalysis', label: 'Urinalysis' },
  { key: 'specimenCollection', label: 'Specimen collection [throat, urine, blood, stool]' },
  { key: 'stapleSuturePlacement', label: 'Staple/suture placement' },
  { key: 'stapleSutureRemoval', label: 'Staple/suture removal' },
  { key: 'surgicalIncisionClosure', label: 'Surgical incision/closure' },
  { key: 'testicularExam', label: 'Testicular exam' },
  { key: 'venipuncture', label: 'Venipuncture' },
  { key: 'visualAcuityTesting', label: 'Visual acuity testing' },
  { key: 'woodsLampFluoresceinEyeExam', label: 'Woods lamp/fluorescein eye exam' },
  { key: 'woundDebridement', label: 'Wound debridement' },
];

const initFromData = (keys, data) =>
  Object.fromEntries(keys.map(({ key }) => [key, data?.[key] ?? false]));

const SkillsForm = forwardRef(({ formData, onSave, disabled = false }, ref) => {
  const [learningOutcomes, setLearningOutcomes] = useState(() => initFromData(LEARNING_OUTCOMES, formData));
  const [technicalSkills, setTechnicalSkills] = useState(() => initFromData(TECHNICAL_SKILLS, formData));
  const [technicalSkillsOther, setTechnicalSkillsOther] = useState('');

  useEffect(() => {
    setLearningOutcomes(initFromData(LEARNING_OUTCOMES, formData));
    setTechnicalSkills(initFromData(TECHNICAL_SKILLS, formData));
    setTechnicalSkillsOther(formData?.technicalSkillsOther ?? '');
  }, [formData]);

  useImperativeHandle(ref, () => ({ validateForm: () => 0 }));

  return (
    <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>Learning Outcomes/Skills</h4>
        <p className={styles.sectionSubtitle}>Select all that apply</p>
        <div className={styles.checkboxGrid}>
          {LEARNING_OUTCOMES.map(({ key, label }) => (
            <Checkbox
              key={key}
              label={label}
              checked={learningOutcomes[key]}
              onChange={() => {
                const value = !learningOutcomes[key];
                setLearningOutcomes((prev) => ({ ...prev, [key]: value }));
                onSave({ [key]: value });
              }}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>Technical Skills</h4>
        <p className={styles.sectionSubtitle}>Select all that apply</p>
        <div className={styles.checkboxGrid}>
          {TECHNICAL_SKILLS.map(({ key, label }) => (
            <Checkbox
              key={key}
              label={label}
              checked={technicalSkills[key]}
              onChange={() => {
                const value = !technicalSkills[key];
                setTechnicalSkills((prev) => ({ ...prev, [key]: value }));
                onSave({ [key]: value });
              }}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
      <div className={styles.section}>
        <TextAreaField
          label="Other"
          value={technicalSkillsOther}
          onChange={(value) => setTechnicalSkillsOther(value)}
          onBlur={() => onSave({ technicalSkillsOther })}
          limit={100}
          disabled={disabled}
        />
      </div>
    </form>
  );
});

SkillsForm.displayName = 'SkillsForm';

SkillsForm.propTypes = {
  formData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default SkillsForm;
